// Guest-facing content for the loaded resort (from resorts/<id>/content.json).
// Exports are live bindings filled by initContent(), so every module sees the same data.
import { t as textOf } from './settings/index.js';

export let CATEGORIES = [];
export let UNIT_TYPES = {};
export let PLACES = {};
export let LISTED = [];
export let GENERAL = {};
let UNITS = {};

// ---- WS-49 texts (begin): the bundle's content overlay and languages (contract section 6). A translatable field is
// an object keyed by language tag; everything leaves this module as the text of the language on screen, so a card
// never meets an object. A plain string is the map's default language, as before.
let SRC = null; // the merged content and its inputs, kept for a language change
let LANG = 'en', DEF = 'en', LANGS = ['en'];
const TAG = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isText = (v) => isObj(v) && Object.keys(v).some((k) => LANGS.includes(k))
  && Object.entries(v).every(([k, s]) => (k === '_auto' ? Array.isArray(s) : TAG.test(k) && typeof s === 'string'));
const say = (v) => (isText(v) ? textOf(v, LANG, DEF) : Array.isArray(v) ? v.map(say) : isObj(v) ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, say(x)])) : v);
// overlay over the package: objects merge, anything else replaces; a translation laid over a plain string keeps
// that string as the text of the package's own language (the first of the map's languages; a preset may open in
// another one, which does not change the language the package was written in)
// (security review FE-2, 2026-10-14: a key that reaches an object's prototype is never walked into or written)
const UNSAFE_KEY = new Set(['__proto__', 'constructor', 'prototype']);
function overlay(target, patch) {
  for (const [k, v] of Object.entries(patch)) {
    if (UNSAFE_KEY.has(k)) continue;
    const cur = target[k];
    if (isText(v) && typeof cur === 'string') target[k] = { [LANGS[0]]: cur, ...v };
    else if (isObj(v) && isObj(cur) && !isText(v)) overlay(cur, v);
    else target[k] = v;
  }
}
/** The language on screen, the map's default language and the languages the map offers. Re-reads the content. */
export function setContentLang(lang, def = DEF, langs = LANGS) {
  LANG = lang || def || 'en'; DEF = def || 'en'; LANGS = Array.isArray(langs) && langs.length ? langs : [DEF];
  if (SRC) initContent(...SRC);
}
/** The content read again from what it was last read with (its documents changed in place: a card made by the
 *  touches, lazy/pathrides.js addCards, so the overlay is laid over it as over any place). Nothing read yet: nothing. */
export function refreshContent() { if (SRC) initContent(...SRC); }
/** The content document read last (the season's, before its overlays), or null: nothing read yet. */
export const contentDoc = () => SRC?.[0] || null;
// ---- WS-49 texts (end)

/**
 * @param content parsed content.json  @param resort parsed resort.json  @param base URL prefix of the resort folder
 * @param [over] the bundle's `content` section (or a list of overlays, applied in order)
 * @type {import('./settings/typedefs.js').InitContent}
 */
export function initContent(content, resort, base, over) {
  SRC = [content, resort, base, over];
  const layers = [over].flat().filter(isObj);
  if (layers.length) { content = structuredClone(content); for (const o of layers) overlay(content, o); }
  const rel = (p) => (/^(https?:|\/)/.test(p) ? p : base + p);
  const fix = (o) => {
    if (o && typeof o.image === 'string' && o.image) o.image = rel(o.image); // VIS-128: an empty image stays none, not the package folder
    // WS-49 galleries: an item is a path or { src, alt? } inside the package; { asset } items are the platform's
    if (Array.isArray(o?.gallery)) o.gallery = o.gallery.map((g) => (typeof g === 'string' ? rel(g) : g && typeof g.src === 'string' ? { ...g, src: rel(g.src) } : g));
    return o;
  };
  CATEGORIES = say(content.categories);
  UNIT_TYPES = Object.fromEntries(Object.entries(content.unitTypes).map(([k, v]) => [k, fix(say(v))]));
  PLACES = Object.fromEntries(Object.entries(content.places).map(([k, v]) => [k, fix(say(v))]));
  LISTED = content.listed.filter((k) => PLACES[k]);
  GENERAL = say(content.general);
  UNITS = Object.fromEntries(Object.entries(resort.units).filter(([k]) => !k.startsWith('_')));
}

/** Which unit type a key belongs to ('cabin', 'towerSuite', ...) and its number, or null. The number is the pattern's
 *  first group, or, when the keys do not hold the resort's own numbers (site-plan keys `aurora-room-a1`), the door
 *  number `numberOf` gives that group ({ "a1": 15, ... }); a group it does not list is no unit of the type. */
export function unitOf(key) {
  if (!key) return null;
  let match = null;
  for (const [type, u] of Object.entries(UNITS)) {
    const m = u?.pattern && key.match(new RegExp(u.pattern)); // VIS-128: a type without a pattern matches no key (not every one, "Cabin NaN")
    if (!m) continue;
    const n = u.numberOf ? u.numberOf[m[1]] : Number(m[1]);
    if (u.numberOf && !Number.isInteger(n)) continue;
    if (u.numbers) { if (u.numbers.includes(n)) return { type, number: n }; continue; }
    match = match || { type, number: n };
  }
  return match;
}

/**
 * VIS-265 Film's `filmAim` for a unit type: all its units together, from each unit's aim (main.js `placeAim`:
 * `target` [x, y, z] = its anchor at its base + 2, `size` its rough radius). The centre is the middle of their targets;
 * the size a radius that holds them all: the farthest target from the middle plus the biggest unit's own size.
 * No units: null. Pure (plain arrays).
 * @param {({ target: number[], size: number } | null)[]} aims  @returns {{ center: number[], size: number } | null}
 */
export function unitGroup(aims) {
  const a = aims.filter(Boolean);
  if (!a.length) return null;
  const center = [0, 1, 2].map((i) => a.reduce((s, o) => s + o.target[i], 0) / a.length);
  const far = Math.max(...a.map((o) => Math.hypot(...o.target.map((v, i) => v - center[i]))));
  return { center, size: far + Math.max(...a.map((o) => o.size)) };
}

/** Resolve a key (cabin-7, suite-104, restaurant, ...) to its card data. */
export function placeForKey(key) {
  if (!key) return null;
  const u = unitOf(key);
  if (u && UNIT_TYPES[u.type]) {
    const t = UNIT_TYPES[u.type];
    return { id: key, unit: u.type, number: u.number, category: 'stay', title: `${t.name} ${u.number}`, ...t };
  }
  return PLACES[key] ? { id: key, ...PLACES[key] } : null;
}

// ---- WS-55 place levels (begin): how a place shows on the map. Four levels, from nothing to everything:
//   hidden  not drawn (places.hidden / categories.hidden: content.js never sees it, the content is filtered upstream)
//   scenery drawn, but nothing happens on hover or press, no label, not in the list
//   named   drawn, its name on hover and its card on a press, no label (today's place without a `label`)
//   marked  drawn with its label (icon and words, labels.<category>), name on hover, card (today's labelled place)
// A preset moves a key with the lists places.scenery, places.named and places.marked (the strongest wins: scenery,
// then named, then marked). Without one the content decides: a unit or a place with a `label` is marked, another
// place is named, a key with no content (a building nobody has named) is scenery. A level above scenery needs
// something to show: a key without content stays scenery whatever the lists say.
// A unit follows its own key first, then its unit type's (a whole type set to "named" at once).
const listed = (values, key) => ['scenery', 'named', 'marked'].find((name) => Array.isArray(values?.[`places.${name}`]) && values[`places.${name}`].includes(key)) || null;
/** @param {string} key  @param {object} [values] the resolved settings (runtime.get()) @returns {'scenery'|'named'|'marked'} */
export function placeLevel(key, values) {
  const p = placeForKey(key);
  if (!p) return 'scenery';
  return listed(values, key) || (p.unit && listed(values, p.unit)) || (p.unit || p.label ? 'marked' : 'named');
}
// ---- WS-55 place levels (end)
